/**
 * Messages the operator copy-pastes when dispatching a session BY HAND from
 * the Admin panel — Snapchat, Instagram, Telegram, or any platform other than
 * WhatsApp (WhatsApp has its own Meta-approved templates and is untouched by
 * this file).
 *
 * The message includes a short, teasing preview of the session's first
 * question, built from the actual words the sender typed:
 *   - question has 5+ words  -> show the first 3 words, then "…?"
 *   - question has < 5 words -> show the first 2 words, then "…?"
 * A one-word question just shows that one word plus "…?".
 *
 * Everything else in the message is fixed text and does not change per
 * session.
 */

const GREETING_EMOJI = '👋🏽';

function cleanName(name?: string | null): string {
  const cleaned = (name ?? '').replace(/\s+/g, ' ').trim().slice(0, 60);
  return cleaned || 'there';
}

// Turns a question's raw text into the short teaser shown in the dispatch
// message: the first 2 or 3 words (by the rule above), trimmed of any
// trailing punctuation of its own, then "…?".
// Shown instead of a text teaser when the first question has no typed text
// at all (a voice-only question). Returned already complete (with its own
// emoji), since it reads oddly with the "👀" that follows a text teaser.
const VOICE_ONLY_TEASER = 'Someone recorded something for you 🎙️';

// Shared word-picking logic. Returns null for a voice-only question (no
// typed text), otherwise the trimmed teaser words joined with "…?" and NO
// trailing emoji — callers decide whether/what to append.
function pickTeaserWords(questionText?: string | null): string | null {
  const words = (questionText ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return null;

  const wordsToShow = words.length >= 5 ? 3 : 2;
  const taken = words.slice(0, Math.min(wordsToShow, words.length));

  // Drop any punctuation clinging to the last shown word (e.g. "me?", "you,")
  // so we don't end up with something like "Do you still?…?".
  const lastIndex = taken.length - 1;
  taken[lastIndex] = taken[lastIndex].replace(/[.,!?;:…]+$/u, '');

  return `${taken.join(' ')}…?`;
}

// Builds the full teaser LINE (teaser text plus its trailing emoji), so the
// caller never needs to know whether the question was text or voice-only.
// Used for the Snapchat/Instagram/Telegram dispatch message (Admin panel).
export function buildTeaser(questionText?: string | null): string {
  const words = pickTeaserWords(questionText);
  return words === null ? VOICE_ONLY_TEASER : `${words} 👀`;
}

// Same teaser, but WITHOUT a trailing emoji — for the WhatsApp templates.
// Half of the approved invite templates already have their own hardcoded
// emoji right after the teaser slot; adding another here would double it up
// ("Do you still…? 👀 👀"). The other half have no emoji at all there, so
// this keeps every variant's own original decoration (or lack of it) intact.
export function buildWhatsAppTeaser(questionText?: string | null): string {
  const words = pickTeaserWords(questionText);
  return words === null ? VOICE_ONLY_TEASER : words;
}

export function sessionLink(testId?: string | null): string {
  return `https://yhuu.life/test/${testId ?? ''}`;
}

// True once the sender has sent their one follow-up question and it's
// sitting unanswered — i.e. this session needs a NEW manual dispatch, not
// its original one. answers.length < questions.length, but only once real
// answers already exist (otherwise it's just a normal never-sent session).
export function isFollowUpDispatch(test: {
  follow_up_used?: boolean;
  questions?: Array<unknown> | null;
  answers?: Array<unknown> | null;
}): boolean {
  const answered = test.answers?.length ?? 0;
  const asked = test.questions?.length ?? 0;
  return !!test.follow_up_used && answered > 0 && asked > answered;
}

// Picks the right dispatch message for whichever case a session is in.
export function getDispatchMessage(test: Parameters<typeof buildDispatchMessage>[0] & Parameters<typeof buildFollowUpDispatchMessage>[0]): string {
  return isFollowUpDispatch(test) ? buildFollowUpDispatchMessage(test) : buildDispatchMessage(test);
}

export function buildDispatchMessage(test: {
  id?: string | null;
  partner_name?: string | null;
  questions?: Array<{ text?: string | null }> | null;
}): string {
  const name = cleanName(test.partner_name);
  const link = sessionLink(test.id);
  const teaser = buildTeaser(test.questions?.[0]?.text);

  return (
    `Hey ${name} ${GREETING_EMOJI}\n\n` +
    `Someone close to you asked something they were afraid to ask you directly…\n\n` +
    `${teaser}\n\n` +
    `Tap the link below to reply 👇\n\n` +
    `${link}\n\n` +
    `Yhuu is for anonymous questions & fun with friends.\n\n` +
    `Under 18? Please ignore this message.\n\n` +
    `— Team Yhuu`
  );
}

// A session that's already been answered, but the recipient opted into a
// follow-up and the sender has now sent it: this re-dispatch needs to remind
// the recipient of the whole thread — their original answers plus the new
// question — since it may be days later and they won't remember the context.
export function buildFollowUpDispatchMessage(test: {
  id?: string | null;
  partner_name?: string | null;
  questions?: Array<{ id: string; text?: string | null }> | null;
  answers?: Array<{ questionId: string; partnerAnswer?: string | null; skipped?: boolean }> | null;
}): string {
  const name = cleanName(test.partner_name);
  const link = sessionLink(test.id);
  const questions = test.questions ?? [];
  const answers = test.answers ?? [];

  const recap = answers
    .map(a => {
      const q = questions.find(q => q.id === a.questionId);
      if (!q) return null;
      const qText = (q.text ?? '').trim() || '(voice question)';
      const aText = a.skipped ? '(skipped)' : (a.partnerAnswer ?? '').trim() || '…';
      return `Q: ${qText}\nA: ${aText}`;
    })
    .filter(Boolean)
    .join('\n\n');

  // The new question is whatever comes right after the ones already answered.
  const newQuestion = questions[answers.length];
  const newQText = (newQuestion?.text ?? '').trim() || 'Someone recorded something for you 🎙️';

  return (
    `Hey ${name} ${GREETING_EMOJI}\n\n` +
    `Remember your Yhuu session? Here's what was asked before:\n\n` +
    `${recap}\n\n` +
    `They said you're open to one more question, and here it is 👀\n\n` +
    `${newQText}\n\n` +
    `Tap the link below to reply 👇\n\n` +
    `${link}\n\n` +
    `— Team Yhuu`
  );
}
